import { useCallback, useMemo, useRef, useState } from 'react'
import { HighchartsReactRefObject } from '@highcharts/react'
import { Grid, LinearProgress, useTheme } from '@mui/material'

import useSharedStyles from '../../../../utils/shared.styles'

import BaseChart from './base'
import DialogLoadAll from './dialog-load-all'
import { defaultDataProps, getAlertPlotBands, getAlertPlotLines, useFetchChartData } from './helpers'

const LineChart = ({
  chart,
  title,
  deviceId,
  channels,
  createdAt,
  alerts,
  isSignalChart,
}: {
  chart: string
  title: string
  deviceId: number
  channels: ChannelType[]
  createdAt: string
  alerts: IAlertHistory[]
  isSignalChart?: boolean
}) => {
  const theme = useTheme()
  const sharedClasses = useSharedStyles
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const chartRef = useRef<HighchartsReactRefObject>(null)
  const { data, isLoading, isBackgroundLoading, fetchData, timeRangeLoaded } = useFetchChartData(deviceId, channels, createdAt, chart)
  const plotBands = useMemo(() => getAlertPlotBands({ deviceId, alerts, channels, chartRef, theme }), [deviceId, alerts, channels, chartRef, theme])
  const plotLines = useMemo(() => getAlertPlotLines({ alerts, channels, theme }), [alerts, channels, theme])

  const clickRangeBtn = (buttonIndex: number) => {
    setTimeout(() => {
      // @ts-expect-error - rangeSelector exists
      chartRef?.current?.chart.rangeSelector.clickButton(buttonIndex, true)
    }, 500)
  }
  const onRangeBtnClick = useCallback((buttonIndex: number | undefined = undefined) => {
    chartRef?.current?.chart.showLoading()
    if (buttonIndex !== undefined) clickRangeBtn(buttonIndex)
  }, [])

  const chartData = useMemo(() => {
    if (data.length === 0) return null

    const seriesNames = data.map((d) => d.seriesName).join(', ')
    const chartDescription = `Line chart showing ${seriesNames} over time.`

    return {
      ...defaultDataProps,
      chart: {
        ...defaultDataProps?.chart,
        type: 'line',
        events: {
          render: () => {
            chartRef?.current?.chart?.hideLoading()
          },
        },
      },
      title: {
        ...defaultDataProps.title,
        text: title,
      },
      accessibility: {
        ...defaultDataProps.accessibility,
        description: chartDescription,
      },
      rangeSelector: {
        allButtonsEnabled: true,
        buttons: [
          {
            type: 'day',
            count: 1,
            text: '1d',
            title: 'View 1 day',
            events: {
              click: () => onRangeBtnClick(0),
            },
          },
          {
            type: 'week',
            count: 1,
            text: '1w',
            title: 'View 1 week',
            dataGrouping: {
              groupPixelWidth: 15,
            },
            events: {
              click: () => onRangeBtnClick(1),
            },
          },
          {
            type: 'month',
            count: 1,
            text: '1m',
            title: 'View 1 month',
            dataGrouping: {
              groupPixelWidth: 30,
            },
            events: {
              click: () => onRangeBtnClick(2),
            },
          },
          {
            type: 'month',
            count: 3,
            text: '3m',
            title: 'View 3 months',
            events: {
              click: () => onRangeBtnClick(3),
            },
          },
          {
            type: 'month',
            count: 6,
            text: '6m',
            title: 'View 6 months',
            dataGrouping: {
              groupPixelWidth: 20,
            },
            events: {
              click: async () => {
                onRangeBtnClick()
                if (timeRangeLoaded === '3m') {
                  await fetchData('6m')
                  clickRangeBtn(4)
                }
              },
            },
          },
          {
            type: 'all',
            text: 'All',
            title: 'View all',
            dataGrouping: {
              groupPixelWidth: 20,
            },
            events: {
              click: () => {
                onRangeBtnClick()
                if (timeRangeLoaded !== 'all') setShowDialog(true)
              },
            },
          },
        ],
      },
      yAxis: data.map((d, i) => ({
        title: {
          text: d.yAxisTitle,
        },
        gridLineWidth: i === 0 ? 1 : 0,
        opposite: i !== 0,
        ...(isSignalChart && {
          reversed: d.unit !== 'dB',
          minorGridLineWidth: 0,
          gridLineWidth: 0,
          ...(d.unit === 'dBm' && {
            plotBands: [
              {
                from: 110,
                to: 120,
                color: '#FEEBEE',
              },
              {
                from: 100,
                to: 110,
                color: '#FFF3E0',
              },
              {
                from: 85,
                to: 100,
                color: '#E8F5E9',
              },
              {
                from: 70,
                to: 85,
                color: '#C8E6C9',
              },
              {
                from: 0,
                to: 70,
                color: '#A5D6A7',
              },
            ],
          }),
        }),
      })),
      xAxis: {
        crosshair: true,
        plotBands,
        plotLines,
      },
      series: [
        ...data.map((d) => ({
          name: d.seriesName,
          type: 'line',
          data: d.data,
          tooltip: {
            valuePrefix: d.unit === 'dBm' ? '-' : '',
            valueSuffix: d.unit,
          },
        })),
      ],
    }
  }, [data, title, plotBands, plotLines, onRangeBtnClick, timeRangeLoaded, fetchData, isSignalChart])
  return (
    <Grid container={true} sx={[sharedClasses.centerContentVertical, { flexDirection: 'column' }]}>
      {isBackgroundLoading && !isLoading && <LinearProgress sx={{ width: '850px', marginLeft: '36px' }} />}
      <BaseChart chartData={chartData as unknown as Highcharts.Options} chartRef={chartRef} isLoading={isLoading} />
      <DialogLoadAll
        open={showDialog && timeRangeLoaded !== 'all'}
        onClose={() => setShowDialog(false)}
        onContinue={async () => {
          setShowDialog(false)
          await fetchData('all')
          clickRangeBtn(5)
        }}
      />
    </Grid>
  )
}

export default LineChart
